import { useEffect, useState } from "react";

import {
  AspectRatio,
  Box,
  Button,
  Center,
  Grid,
  Group,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { Dropzone, FileWithPath, MIME_TYPES } from "@mantine/dropzone";
import { FileRejection } from "react-dropzone";
import BoxContainer from "~/../../base-components/lib/BoxContainer";
import {
  Image01,
  Upload01,
  X,
} from "~/../../base-components/lib/assets/icons/HDS";
import { SECONDARY_COLOR } from "~/../../base-components/lib/theme/button";
import {
  STATUS_COLOR,
  TEXT_COLOR,
} from "~/../../base-components/lib/theme/colors";
import { SupportedMediaType } from "~/../../communities-app/lib/components/ContentFeed/TextEditor/types";
import { logError } from "~/../../shared-features/lib/services/logger";

import { SupportedMediaMime } from "apollo/graphql.generated";
import { mimeFromType, sizeMbByType, typeFromMime } from "utils/media/file";
import {
  UPLOAD_ERROR,
  uploadToS3,
} from "~/features/collections/forms/CreateCollectionMultistepForm/CollectionMediaUploader";
import { useGetUploadUrlForContractUriMutation } from "~/features/tokens/queries/hosted-media.graphql.generated";

type BuilderMediaUploaderProps = {
  mediaUrl?: string;
  mediaError?: string;
  mediaType: SupportedMediaType;
  onMediaChanged: (media?: { url: string; type: string } | null) => void;
  edited?: boolean;
};

export const BuilderMediaUploader = (props: BuilderMediaUploaderProps) => {
  const {
    mediaUrl,
    mediaError: errorFromProps,
    onMediaChanged,
    edited = false,
  } = props;

  const [media, setMedia] = useState<FileWithPath | null>(null);
  const [localMediaUrl, setLocalMediaUrl] = useState<string | undefined>(
    mediaUrl
  );
  const [mediaError, setMediaError] = useState<string | undefined>(
    errorFromProps
  );
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);
  const [getUploadUrl] = useGetUploadUrlForContractUriMutation();
  const [rejections, setRejections] = useState<FileRejection[]>([]);

  useEffect(() => {
    setMediaError(errorFromProps);
  }, [errorFromProps]);

  const onFileSelected = (files: FileWithPath[]) => {
    const file = files[0];
    const mediaType = typeFromMime[file.type];
    const maxSizeInMb = sizeMbByType[mediaType];
    if (file.size > maxSizeInMb * 1000000) {
      const error = `The file size cannot be greater than ${maxSizeInMb}MB.`;
      setMediaError(error);
    } else {
      setMedia(file);
      setMediaError("");
    }
  };

  const clearAllAttachments = () => {
    setMedia(null);
    setMediaError("");
    onMediaChanged(null);
  };

  const getMediaUrls = async (mime: SupportedMediaMime) => {
    const getUrlResponse = await getUploadUrl({
      variables: { data: { mime } },
    });

    const metadata = getUrlResponse?.data?.getUploadUrlForContractUri?.metadata;
    if (!metadata) {
      throw new Error(
        `[CollectionMediaUploader] Unexpected response from getUploadUrlForContractUri: ${JSON.stringify(
          { getUrlResponse }
        )}`
      );
    }

    const { url, publicUrl } = metadata;
    if (!url || !publicUrl) {
      throw new Error(
        `[CollectionMediaUploader] Unexpected upload media metadata: ${JSON.stringify(
          { metadata }
        )}`
      );
    }

    return { url, publicUrl };
  };

  /**
   * Upload media files and replaces the local blob URLs with the public CDN ones.
   * @param file file to be uploaded
   */
  const uploadMediaFileToS3 = async (file: File) => {
    try {
      setIsMediaUploading(true);
      const { url, publicUrl } = await getMediaUrls(
        mimeFromType[file.type] as SupportedMediaMime
      );
      await uploadToS3(file, url);
      onMediaChanged({ url: publicUrl, type: file.type });
      setMedia(null);
      // const response = {
      //   url: "https://highlight-creator-assets.highlightstaging.xyz/main/image/fe9f329d-1f51-4968-8045-3d6d72074532.jpeg",
      //   type: "image/jpeg",
      // };
      // onMediaChanged(response);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err) {
      logError({
        err,
        message: "[CollectionMediaUploader] Failed to fetch upload URL",
      });

      setMediaError(UPLOAD_ERROR);
      setMedia(null);
      onMediaChanged(null);
    } finally {
      setIsMediaUploading(false);
    }
  };

  useEffect(() => {
    if (media) {
      setLocalMediaUrl(URL.createObjectURL(media));
      uploadMediaFileToS3(media); // runs async
    }
  }, [media]);

  const mediaPreview = (mediaUrl || media) &&
    localMediaUrl &&
    !isMediaUploading && (
      <>
        <Image
          src={localMediaUrl}
          height={"64px"}
          width={"64px"}
          fit="contain"
          imageProps={{ onLoad: () => URL.revokeObjectURL(localMediaUrl) }}
        />
      </>
    );
  const supportedTypes = [
    MIME_TYPES.jpeg,
    MIME_TYPES.png,
    MIME_TYPES.webp,
    MIME_TYPES.gif,
  ];

  const mediaUpload = (!media || isMediaUploading) && (
    <Box p={0}>
      <Group>
        <Dropzone
          multiple={false}
          accept={supportedTypes}
          onDrop={async (files) => {
            setRejections([]);
            onFileSelected(files);
          }}
          onReject={(files) => {
            setRejections(files);
          }}
          loading={isMediaUploading}
          sx={(theme) => ({
            borderWidth: "1px",
            borderColor: mediaError
              ? theme.colors.errorStatus[0]
              : theme.colors.divider[0],
            borderStyle: "solid",
            minWidth: "64px",
            minHeight: "64px",
            maxWidth: "64px",
            maxHeight: "64px",
          })}
          p={0}
        >
          <AspectRatio ratio={1}>
            <Stack
              align="center"
              justify="center"
              spacing={0}
              style={{ pointerEvents: "none" }}
            >
              <Dropzone.Accept>
                <Upload01 height={24} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <X height={24} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                {/* <IconPhoto
                  css={css`
                    width: 24px;
                    height: 24px;
                  `}
                /> */}
                <Image01 height={24} />
              </Dropzone.Idle>
            </Stack>
          </AspectRatio>
        </Dropzone>

        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          Upload an image...
        </Text>
      </Group>
      {rejections.map((rejection: FileRejection) => (
        <>
          <Text
            key={rejection.file.name + rejection.file.lastModified}
            mt="sm"
            size="sm"
            color={STATUS_COLOR.ERROR}
          >
            Error for {rejection.file.name}:
          </Text>
          {rejection.errors.map((error) => (
            <Text
              key={error.code}
              mt="sm"
              size="sm"
              color={STATUS_COLOR.ERROR}
              sx={() => ({ overflowWrap: "break-word" })}
            >
              {error.message}
            </Text>
          ))}
        </>
      ))}
    </Box>
  );

  return (
    <Stack spacing={4}>
      <BoxContainer
        sx={(theme) => ({
          borderWidth: "1px",
          borderColor: mediaError
            ? theme.colors.errorStatus[0]
            : theme.colors.divider[0],
          borderStyle: "solid",
        })}
      >
        <Grid align="center" justify="space-between">
          <Grid.Col span={6}>
            {!mediaPreview && mediaUpload}
            {mediaPreview}
          </Grid.Col>
          <Grid.Col span={4} offset={2}>
            <Center sx={{ justifyContent: "right" }}>
              {edited && mediaUrl && (
                <Button
                  size="sm"
                  type={"submit"}
                  color={SECONDARY_COLOR}
                  px={12}
                  onClick={() => clearAllAttachments()}
                >
                  <Text size="xs">Remove</Text>
                </Button>
              )}
              {!edited && mediaUrl && (
                <Button
                  size={"lg"}
                  type={"submit"}
                  color={SECONDARY_COLOR}
                  px={12}
                  onClick={() => clearAllAttachments()}
                >
                  Upload custom logo
                </Button>
              )}
            </Center>
          </Grid.Col>
        </Grid>
      </BoxContainer>
      {mediaError && (
        <Text size="xs" color={STATUS_COLOR.ERROR}>
          {mediaError}
        </Text>
      )}
    </Stack>
  );
};
